import { RefObject, useCallback, useRef } from 'react';
import { Canvas, StaticCanvas } from 'fabric';
import type { CanvasJSON, FieldBinding, ResolutionConfig } from '@doer/frontend-components/creativity-editor';
import { prepareCanvasJson } from '@doer/frontend-components/creativity-editor';
import { applyAllBindings, applyBindingsToJson } from '../binding';
import { RoutedFormData } from 'src/types/templates';

type ComputeDefaults = (data: RoutedFormData, resolutionId?: string) => RoutedFormData;

type UseResolutionSnapshotsParams = {
  canvas: Canvas | null;
  resolutions: ResolutionConfig[];
  activeResolutionId: string;
  fieldBindings: FieldBinding[];
  formDataRef: RefObject<RoutedFormData>;
  computeDefaults: ComputeDefaults;
};

type ResolutionSnapshotsApi = {
  save: (resolutionId: string) => void;
  get: (resolutionId: string) => CanvasJSON | undefined;
  patchOthers: (data: RoutedFormData) => void;
  syncObjectAcrossResolutions: (pieceId: string, props: Record<string, unknown>) => void;
  resolveResolutionJson: (resolution: ResolutionConfig) => Promise<CanvasJSON>;
};

/**
 * Owns the in-memory snapshot store (per-resolution JSON) and the operations that read/write it.
 * Snapshots preserve user edits (moves/scales) across resolution switches.
 *
 * resolveResolutionJson is async: for inactive resolutions it materializes the JSON in an
 * off-screen StaticCanvas running applyAllBindings end-to-end.
 */
export const useResolutionSnapshots = ({
  canvas,
  resolutions,
  activeResolutionId,
  fieldBindings,
  formDataRef,
  computeDefaults
}: UseResolutionSnapshotsParams): ResolutionSnapshotsApi => {
  const snapshotsRef = useRef<Map<string, CanvasJSON>>(new Map());

  const save = useCallback(
    (resolutionId: string) => {
      if (!canvas) return;
      const json = canvas.toJSON() as CanvasJSON;

      snapshotsRef.current.set(resolutionId, json);
    },
    [canvas]
  );

  const get = useCallback((resolutionId: string): CanvasJSON | undefined => snapshotsRef.current.get(resolutionId), []);

  // Propagates the latest formData into inactive snapshots, keeping their geometry intact.
  const patchOthers = useCallback(
    (data: RoutedFormData) => {
      resolutions.forEach(resolution => {
        if (resolution.id === activeResolutionId) return;
        const snap = snapshotsRef.current.get(resolution.id);
        if (!snap) return;
        const defaults = computeDefaults(data, resolution.id);
        snapshotsRef.current.set(
          resolution.id,
          applyBindingsToJson(snap, data, fieldBindings, resolution.id, defaults)
        );
      });
    },
    [resolutions, activeResolutionId, fieldBindings, computeDefaults]
  );

  // Mirrors a single object's props (e.g. move/scale) to every inactive resolution's snapshot.
  const syncObjectAcrossResolutions = useCallback(
    (pieceId: string, props: Record<string, unknown>) => {
      const data = formDataRef.current;
      resolutions.forEach(resolution => {
        if (resolution.id === activeResolutionId) return;

        const existing = snapshotsRef.current.get(resolution.id);
        const baseJson =
          existing ??
          applyBindingsToJson(
            resolution.layout,
            data,
            fieldBindings,
            resolution.id,
            computeDefaults(data, resolution.id)
          );

        const updatedObjects = baseJson.objects.map(obj => {
          if (obj.pieceId !== pieceId) return obj;
          if (obj.linkedAcrossResolutions === false) return obj;
          return { ...obj, ...props };
        });

        snapshotsRef.current.set(resolution.id, { ...baseJson, objects: updatedObjects });
      });
    },
    [resolutions, activeResolutionId, fieldBindings, computeDefaults, formDataRef]
  );

  const materializeInactiveJson = useCallback(
    async (resolution: ResolutionConfig): Promise<CanvasJSON> => {
      const data = formDataRef.current;
      const defaults = computeDefaults(data, resolution.id);
      const base = snapshotsRef.current.get(resolution.id) ?? resolution.layout;

      const offscreen = new StaticCanvas(undefined, {
        width: resolution.canvasWidth,
        height: resolution.canvasHeight
      });
      try {
        await offscreen.loadFromJSON(await prepareCanvasJson(base));
        await applyAllBindings(offscreen, data, fieldBindings, resolution.id, defaults);
        return offscreen.toJSON() as CanvasJSON;
      } finally {
        offscreen.dispose();
      }
    },
    [fieldBindings, computeDefaults, formDataRef]
  );

  // Returns the JSON for any resolution. Active: live canvas. Inactive: materialized async.
  const resolveResolutionJson = useCallback(
    async (resolution: ResolutionConfig): Promise<CanvasJSON> => {
      if (resolution.id === activeResolutionId && canvas) {
        return canvas.toJSON() as CanvasJSON;
      }
      return materializeInactiveJson(resolution);
    },
    [canvas, activeResolutionId, materializeInactiveJson]
  );

  return { save, get, patchOthers, syncObjectAcrossResolutions, resolveResolutionJson };
};
